import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { BOOKING_FOCUSED_PARAM, BOOKING_HUB_URL } from '../utils/bookingLinks';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loginUrlWithReturn, SAFETY_FORUM_PATH } from '../utils/safeReturnUrl';
import './TravelCompanionPage.css';

const TravelCompanionPage = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const focusedEntry = searchParams.get(BOOKING_FOCUSED_PARAM) === '1';
  const urlDate = searchParams.get('date') || '';
  const [filterDateFrom, setFilterDateFrom] = useState(searchParams.get('date_from') || urlDate);
  const [filterDateTo, setFilterDateTo] = useState(searchParams.get('date_to') || urlDate);
  const [filterFlightType, setFilterFlightType] = useState(searchParams.get('flight_type') || '');
  const [filterDestination, setFilterDestination] = useState(searchParams.get('destination') || '');
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    date: searchParams.get('date') || '',
    flight_type: searchParams.get('flight_type') || 'city',
    destination: searchParams.get('destination') || '',
    name: '',
    email: '',
    phone: '',
    notes: '',
    preferred_hour_start: '',
    preferred_hour_end: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const loadInterests = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterFlightType) params.flight_type = filterFlightType;
      if (filterDestination?.trim()) params.destination = filterDestination.trim();
      const response = await axios.get('/api/travel-companion/interests', { params });
      setInterests(response.data.interests || []);
    } catch (err) {
      setError(t('companions.errLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInterests();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    loadInterests();
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.date?.trim()) {
      setError(t('companions.errDate'));
      return;
    }
    if (!formData.name?.trim()) {
      setError(t('companions.errName'));
      return;
    }
    if (!formData.email?.trim()) {
      setError(t('companions.errEmail'));
      return;
    }

    const today = new Date().toISOString().slice(0, 10);
    if (formData.date < today) {
      setError(t('companions.errDateFuture'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/travel-companion/interests', {
        date: formData.date.trim(),
        flight_type: formData.flight_type,
        destination: formData.flight_type === 'city_to_city' ? (formData.destination?.trim() || null) : null,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone?.trim() || null,
        notes: formData.notes?.trim() || null,
        preferred_hour_start: formData.preferred_hour_start ? parseInt(formData.preferred_hour_start, 10) : null,
        preferred_hour_end: formData.preferred_hour_end ? parseInt(formData.preferred_hour_end, 10) : null
      });
      setSuccess(t('companions.success'));
      setFormData(prev => ({ ...prev, name: '', email: '', phone: '', notes: '', preferred_hour_start: '', preferred_hour_end: '' }));
      loadInterests();
    } catch (err) {
      setError(err.response?.data?.error || t('companions.errRegistration'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
  };

  const getFlightTypeLabel = (type) => type === 'city_to_city' ? t('companions.cityToCity') : t('companions.budapestSightseeing');

  const groupedInterests = interests.reduce((acc, i) => {
    const key = `${i.date}|${i.flight_type}|${i.destination || ''}`;
    if (!acc[key]) acc[key] = { date: i.date, flight_type: i.flight_type, destination: i.destination, items: [] };
    acc[key].items.push(i);
    return acc;
  }, {});

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="travel-companion-page container">
      <div className="travel-companion-hero" aria-hidden="true">
        <img src="/offer-travel-companions.jpg" alt="" loading="eager" width="1024" height="682" />
      </div>
      <h1 id="travel-companion-heading">{t('companions.title')}</h1>
      <p className="travel-companion-intro">
        {t('companions.intro')}
      </p>

      {!focusedEntry && (
        <div className="travel-companion-booking-hint">
          <Link to={BOOKING_HUB_URL}>{t('nav.bookSlot')}</Link> {t('companions.bookHint')}
          {' · '}
          <Link to="/booking/voucher">{t('companions.voucherHint')}</Link>
          {' · '}
          <Link
            to={isAuthenticated ? SAFETY_FORUM_PATH : loginUrlWithReturn(SAFETY_FORUM_PATH)}
            title={!isAuthenticated ? t('nav.safetyForumLoginHint') : undefined}
          >
            {t('nav.safetyForum')}
          </Link>
        </div>
      )}
      {focusedEntry && (
        <p className="travel-companion-focused-footer">
          <Link to={BOOKING_HUB_URL}>{t('booking.openFullBookingMenu')}</Link>
          {' · '}
          <Link
            to={isAuthenticated ? SAFETY_FORUM_PATH : loginUrlWithReturn(SAFETY_FORUM_PATH)}
            title={!isAuthenticated ? t('nav.safetyForumLoginHint') : undefined}
          >
            {t('nav.safetyForum')}
          </Link>
        </p>
      )}

      {error && <div className="error" role="alert">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="travel-companion-sections">
        <section className="card travel-companion-search">
          <h2>{t('companions.search')}</h2>
          <form onSubmit={handleSearch} className="search-form">
            <div className="form-row">
              <div className="form-group">
                <label>{t('companions.dateFrom')}</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  min={today}
                />
              </div>
              <div className="form-group">
                <label>{t('companions.dateTo')}</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  min={today}
                />
              </div>
              <div className="form-group">
                <label>{t('companions.flightType')}</label>
                <select value={filterFlightType} onChange={(e) => setFilterFlightType(e.target.value)}>
                  <option value="">{t('companions.all')}</option>
                  <option value="city">{t('companions.budapestSightseeing')}</option>
                  <option value="city_to_city">{t('companions.cityToCity')}</option>
                </select>
              </div>
              {filterFlightType === 'city_to_city' && (
                <div className="form-group">
                  <label>{t('companions.destination')}</label>
                  <input
                    type="text"
                    value={filterDestination}
                    onChange={(e) => setFilterDestination(e.target.value)}
                    placeholder={t('companions.destinationPlaceholder')}
                  />
                </div>
              )}
              <button type="submit" className="btn btn-primary">{t('companions.searchBtn')}</button>
            </div>
          </form>

          {loading && <div className="loading">{t('companions.loading')}</div>}
          {!loading && (
            <div className="interests-results">
              {Object.keys(groupedInterests).length === 0 ? (
                <p>{t('companions.noInterests')}</p>
              ) : (
                <div className="interests-groups">
                  {Object.values(groupedInterests).map((group) => (
                    <div key={`${group.date}|${group.flight_type}|${group.destination || ''}`} className="interest-group">
                      <h3>
                        {formatDate(group.date)} – {getFlightTypeLabel(group.flight_type)}
                        {group.destination && <span className="interest-destination"> Budapest → {group.destination}</span>}
                      </h3>
                      <p className="interest-count">{t('companions.personsInterested', { count: group.items.length })}</p>
                      <ul className="interest-list">
                        {group.items.map((item) => (
                          <li key={item.id} className="interest-item">
                            <strong>{item.name}</strong>
                            <a href={`mailto:${item.email}`}>{item.email}</a>
                            {item.phone && <span>{t('companions.tel')}: {item.phone}</span>}
                            {(item.preferred_hour_start != null || item.preferred_hour_end != null) && (
                              <span className="preferred-time">
                                {t('companions.preferred')}: {item.preferred_hour_start != null ? `${String(item.preferred_hour_start).padStart(2, '0')}:00` : '?'}–
                                {item.preferred_hour_end != null ? `${String(item.preferred_hour_end).padStart(2, '0')}:00` : '?'}
                              </span>
                            )}
                            {item.notes && <p className="interest-notes">{item.notes}</p>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="card travel-companion-register">
          <h2>{t('companions.register')}</h2>
          <p className="form-desc">{t('companions.formDesc')}</p>
          <form onSubmit={handleRegister} noValidate>
            <div className="form-group">
              <label>{t('companions.date')} {t('common.required')}</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                min={today}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('companions.flightType')} {t('common.required')}</label>
              <select value={formData.flight_type} onChange={(e) => setFormData({ ...formData, flight_type: e.target.value })}>
                <option value="city">{t('companions.budapestSightseeing')}</option>
                <option value="city_to_city">{t('companions.cityToCity')}</option>
              </select>
              <small className="form-hint" style={{ display: 'block', marginTop: '4px' }}>
                {t('companions.budapestHint')}
              </small>
            </div>
            {formData.flight_type === 'city_to_city' && (
              <div className="form-group">
                <label>{t('companions.destination')}</label>
                <input
                  type="text"
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  placeholder="e.g. Vienna, Bratislava, Lake Balaton"
                />
              </div>
            )}
            <div className="form-group">
              <label>{t('companions.name')} {t('common.required')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('companions.email')} {t('common.required')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('companions.phone')}</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="form-row form-row-inline">
              <div className="form-group">
                <label>{t('companions.preferredFrom')}</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={formData.preferred_hour_start}
                  onChange={(e) => setFormData({ ...formData, preferred_hour_start: e.target.value })}
                  placeholder="9"
                />
              </div>
              <div className="form-group">
                <label>{t('companions.preferredTo')}</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={formData.preferred_hour_end}
                  onChange={(e) => setFormData({ ...formData, preferred_hour_end: e.target.value })}
                  placeholder="12"
                />
              </div>
            </div>
            <div className="form-group">
              <label>{t('companions.notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="2"
                placeholder="e.g. flexible on time, looking for 2 more people"
              />
            </div>
            <button type="submit" className="btn btn-success" disabled={submitting}>
              {submitting ? t('companions.submitting') : t('companions.registerBtn')}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default TravelCompanionPage;
